/**
 * Every in-app URL: the react-router patterns the route table declares, and the
 * builders that produce hrefs for them.
 *
 * Two rules hold here that cannot hold if paths are written inline at each link
 * site:
 *
 * 1. Dynamic routes carry a literal segment before each id
 *    (`/ns/…/sg/…/str/…`), so the id space and the static-route space are
 *    disjoint. A namespace may be called "search" or "home" without becoming
 *    unreachable, and a static route added later cannot retroactively shadow an
 *    existing namespace. Namespaces are self-service, so ids are user-supplied
 *    and this collision is otherwise reachable in normal use.
 * 2. Ids are always percent-encoded. An id containing '/', '?' or '#' would
 *    otherwise rewrite the path it is interpolated into rather than sit inside
 *    a segment of it.
 *
 * Patterns and builders are derived from the same segment constants, so a
 * pattern cannot drift from the href that is supposed to match it.
 */

/** Literal segment that introduces a namespace id. */
const NAMESPACE_SEGMENT = 'ns';
/** Literal segment that introduces a string-group id. */
const STRING_GROUP_SEGMENT = 'sg';
/** Literal segment that introduces a string id. */
const STRING_SEGMENT = 'str';

// --- Static routes ---

/**
 * Landing probe: sends a caller who can see at least one namespace to
 * SEARCH_PATH, and renders the marketing page for one who can see none, or
 * whose probe fails (src/features/landing/LandingEntry.tsx). Not a useful link
 * destination from inside the app — for a signed-in user it only spends a
 * listNamespaces round trip to arrive at search, so link to SEARCH_PATH
 * instead.
 */
export const ROOT_PATH = '/';

/** Marketing landing shell, rendered outside the app chrome. */
export const HOME_PATH = '/home';

export const SEARCH_PATH = '/search';

export const TRACK_MY_TRANSLATIONS_PATH = '/trackMyTranslations';

// --- Dynamic route patterns (for <Route path=…>) ---

export const NAMESPACE_ROUTE = `/${NAMESPACE_SEGMENT}/:namespaceId`;

export const STRING_GROUP_ROUTE = `${NAMESPACE_ROUTE}/${STRING_GROUP_SEGMENT}/:stringGroupId`;

export const STRING_ROUTE = `${STRING_GROUP_ROUTE}/${STRING_SEGMENT}/:stringId`;

/** Matches anything the routes above do not; the route table sends it to ROOT_PATH. */
export const CATCH_ALL_ROUTE = '*';

// --- Href builders ---

/**
 * Arguments are positional in URL order (namespace → string group → string),
 * mirroring both the route patterns and the entity hierarchy.
 */
export const buildNamespacePath = (namespaceId: string): string =>
  `/${NAMESPACE_SEGMENT}/${encodeURIComponent(namespaceId)}`;

export const buildStringGroupPath = (
  namespaceId: string,
  stringGroupId: string,
): string =>
  `${buildNamespacePath(namespaceId)}/${STRING_GROUP_SEGMENT}/${encodeURIComponent(stringGroupId)}`;

export const buildStringPath = (
  namespaceId: string,
  stringGroupId: string,
  stringId: string,
): string =>
  `${buildStringGroupPath(namespaceId, stringGroupId)}/${STRING_SEGMENT}/${encodeURIComponent(stringId)}`;
